// ── Loads the per-company region map once per login (multi-tenant package "money") ───────────────
// utils/region.ts formats a document's amounts / dates in ITS company's region when it is passed the
// company id and that company is in the map. A screen that does so calls useCompanyRegions(): the
// map is fetched once per signed-in user and the screen re-renders when it lands. Until then (or
// when the fetch is refused — e.g. a role without staff routes) every id reads the active region,
// which is exactly what the screens printed before.
import { useEffect, useSyncExternalStore } from 'react';
import { useStore } from '../store';
import { addCompanyRegions, clearCompanyRegions, subscribeCompanyRegions, companyRegionsVersion } from './region';
import { fetchCompanyRegions } from './api/money';

let loadedFor: string | null = null;
/** Fetch the map for this user unless it is already loaded (or loading) for them. */
export function loadCompanyRegions(userId: string | null | undefined): void {
  const uid = String(userId || '');
  if (!uid || loadedFor === uid) return;
  if (loadedFor !== null) clearCompanyRegions();   // another login on this tab: drop the old firms
  loadedFor = uid;
  fetchCompanyRegions()
    .then(r => { if (loadedFor === uid) addCompanyRegions(r?.regions || {}); })
    .catch(() => { /* refused / offline: every id keeps reading the active region */ });
}
/** Ensure the map is loaded for the signed-in user; re-renders the caller when it changes.
 *  Returns the map's version (use it as a memo dependency where amounts are pre-computed). */
export function useCompanyRegions(): number {
  const uid = useStore(s => s.me?.userId);
  useEffect(() => { loadCompanyRegions(uid); }, [uid]);
  return useSyncExternalStore(subscribeCompanyRegions, companyRegionsVersion);
}
